import { useEffect, useRef } from 'react';

/**
 * The white card inside a modal overlay — with the three things a screen reader
 * needs and thirteen hand-written copies of this div did not have.
 *
 * The problem it fixes: with TalkBack on, opening "Delete this event?" changed
 * nothing for a blind user. The reading cursor stayed wherever it was, behind
 * the dark overlay, still walking through the page underneath — a page that is
 * no longer usable, because every tap lands on the overlay. Nothing announced
 * that a question had been asked, and nothing said what the question was.
 *
 * Three lines do all the work:
 *   role="dialog" + aria-modal  tell the screen reader that everything outside
 *                               this card is out of play, so swiping stays in.
 *   tabIndex={-1} + focus()     move the reading cursor INTO the card on open,
 *                               which makes TalkBack read the question aloud.
 *   restore on unmount          put the cursor back where it was, so closing a
 *                               dialog doesn't dump the user at the top of the
 *                               page having lost their place.
 *
 * Drop-in: it renders a plain <div> and forwards style/onClick untouched, so
 * `<div style={...}>` becomes `<DialogCard style={...}>` with no visual change.
 * `label` is for dialogs whose card holds only a picture (the photo preview) —
 * text dialogs need nothing, the focus move reads the text itself.
 */
export default function DialogCard({ label, children, style, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;
    // A frame's grace: focusing before the browser has painted the card can be
    // undone by the layout that follows, and TalkBack would announce nothing.
    const id = requestAnimationFrame(() => {
      try { ref.current?.focus({ preventScroll: true }); } catch (_) {}
    });
    return () => {
      cancelAnimationFrame(id);
      // Only take the cursor back if it is still inside the dialog. If the user
      // has already moved on — a dialog that closes by navigating away — the
      // element they left behind may be gone, and yanking focus to a detached
      // node sends a screen reader back to the top of the document.
      try {
        if (previous && document.contains(previous) && ref.current?.contains(document.activeElement)) {
          previous.focus({ preventScroll: true });
        }
      } catch (_) {}
    };
  }, []);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={label || undefined}
      tabIndex={-1}
      {...rest}
      // After the spread, never before it: the focus ring belongs on buttons,
      // not round a whole card that was focused programmatically, and a caller's
      // own style must not be able to put the outline back.
      style={{ outline: 'none', ...(style || {}) }}
    >
      {children}
    </div>
  );
}
